import { type Accessor, createEffect } from "solid-js";
import Editor from "~/js/editor";
import type { CommentFrameWorkspaceDefinition } from "~/js/nodes/local/logic/commentFrame";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import {
	calculateBoundariesOfNodes,
	calculateBoundariesOfObjectSerialized,
	calculateBoundariesOfObjects,
	moveEditorToBoundary
} from "~/js/utilities/fitToScreenUtils";
import { generate_UUID } from "~/js/utilities/UUID";
import { clearWorkspace, loadWorkspace, saveWorkspace } from "~/js/workspace";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { WorkspaceApi } from "~/solidJs/entities/WorkspaceListEntity/api/WorkspaceApi";
import type {
	IWorkspace,
	IWorkspaceListItem,
	IWorkspaceTemplate
} from "~/solidJs/entities/WorkspaceListEntity/helpers/workspaceTypes";
import {
	getWorkspacesStore,
	type projectsStoreType,
	setIsAutoSavePending,
	setWorkspacesStore
} from "~/solidJs/entities/WorkspaceListEntity/models/wrokspaceListContext";
import { setIsMissingWorkspaceByLinkVisible } from "../models/globalStores";
import { getPrivateMessageFromError } from "./errorHelpers";
import type { IUserTemplateParams } from "./types/INodeDefinition";
import { getIsSaveFunctionalityAvailable } from "./userHelpers";
import { getWokspaceLinkFromPathNameOrLocalStorage } from "./workspaceLinksHelpers";

export const getSharedWorkspaceFromLink = async (pathName: string) => {
	const uuid = getWokspaceLinkFromPathNameOrLocalStorage(pathName);
	if (!uuid) return null;
	try {
		const workapceData = await WorkspaceApi.getWorkspaceByLink(uuid);

		loadWorkspaceAndSetStore(workapceData, true);
		return true;
	} catch (e: any) {
		if (e?.response?.status === 404) {
			setIsMissingWorkspaceByLinkVisible(true);
		}
		console.log(e);
		EventAnalytics.sendErrorInfo("open-shared-workspace", {
			message: getPrivateMessageFromError(e) || "unknowError"
		});
		return null;
	}
};

export const getSharedOrUserWorkspace = (pathName: string) => {
	const userWorkspaceId = getWokspaceLinkFromPathNameOrLocalStorage(
		pathName,
		"user-workspace",
		"userWorkspaceToAdd"
	);
	if (userWorkspaceId) {
		return getUserWorkspaceFromLink(pathName);
	}
	return getSharedWorkspaceFromLink(pathName);
};

export const getUserWorkspaceFromLink = async (pathName: string) => {
	const uuid = getWokspaceLinkFromPathNameOrLocalStorage(pathName, "user-workspace");
	if (!uuid) return null;
	try {
		const workapceData = await WorkspaceApi.getWorkspaceConfigById(uuid);
		loadWorkspaceAndSetStore(workapceData, false);
		return true;
	} catch (e: any) {
		if (e?.response?.status === 404) {
			setIsMissingWorkspaceByLinkVisible(true);
		}
		console.log(e);
		EventAnalytics.sendErrorInfo("open-user-workspace", {
			message: getPrivateMessageFromError(e) || "unknowError"
		});
		return null;
	}
};
export const createAutoSaveWorkpaseEffect = (workspaceStore: Accessor<projectsStoreType>) => {
	let timeout: NodeJS.Timeout;
	const autoSaveHandler = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(async () => {
			setIsAutoSavePending(true);
			try {
				await saveWorkspace(undefined, true);
			} catch (e) {
				EventAnalytics.sendErrorInfo("auto-save-error", {
					message: getPrivateMessageFromError(e)
				});
			}
			setIsAutoSavePending(false);
		}, 15000);
	};
	createEffect(() => {
		const storeValue = workspaceStore();

		if (
			!getIsSaveFunctionalityAvailable(getUserStore()) ||
			storeValue.activeProjectId === null ||
			storeValue.isStateSaved ||
			storeValue.isOpenedByLink ||
			storeValue.isOpenPending
		) {
			clearTimeout(timeout);
			return;
		}

		autoSaveHandler();
	});
};

export const loadTemplate = async (id: number) => {
	const workapceData = await WorkspaceApi.getOnboardingTemplate(id);
	loadTemplateToFreeSpot(workapceData);
};

export const loadTemplateToFreeSpot = async (template: IWorkspaceTemplate) => {
	mutWorkspaceLeagcyData(template.WorkspaceTemplate.workspace);
	mutWorkspaceToFreeSpace(template.WorkspaceTemplate.workspace);
	updateWorkspaceUUids(template.WorkspaceTemplate.workspace);
	loadWorkspaceAndSetStore(
		template.WorkspaceTemplate,
		false,
		(workspace) => ({
			templateName: workspace.name,
			isStateSaved: true
		}),
		false
	);
};

export const getTemplateFromLink = async (pathName: string) => {
	const uuid =
		getWokspaceLinkFromPathNameOrLocalStorage(pathName, "template", "templateToAdd") ||
		getWokspaceLinkFromPathNameOrLocalStorage(pathName, "task", "taskToAdd");
	if (!uuid) return null;
	try {
		loadTemplate(Number(uuid));

		return true;
	} catch (e: any) {
		if (e?.response?.status === 404) {
			setIsMissingWorkspaceByLinkVisible(true);
		}
		console.log(e);
		EventAnalytics.sendErrorInfo("open-shared-template", {
			message: getPrivateMessageFromError(e) || "unknowError"
		});
		return null;
	}
};

/**
 * util function that help open workspace and set active workpace store
 * @param workspace workspace data with config
 * @param isOpenedByLink used to prevent some functionality on projects oppened via shared link
 */
export const loadWorkspaceAndSetStore = async (
	workspace: IWorkspaceListItem,
	isOpenedByLink = false,
	workspaceStoreSetter?: (workspace: IWorkspaceListItem) => Partial<projectsStoreType>,
	isNeedToClear = true
) => {
	try {
		setWorkspacesStore((prev) => ({
			...prev,
			...(workspaceStoreSetter
				? workspaceStoreSetter(workspace)
				: {
						activeProjectId: workspace.id,
						activeProjectName: workspace.name,
						activeWorkspaceLink: workspace.sharing_link || null,
						isOpenedByLink: isOpenedByLink,
						isStateSaved: true,
						templateName: undefined
					})
		}));
		if (isNeedToClear) {
			await clearWorkspace();
		}
		await loadWorkspace(workspace.workspace, workspace.file_id_list, workspace.id);
	} catch (e) {
		console.log(e);
		EventAnalytics.sendErrorInfo("load-workspace-and-set-store-fc-error", {
			message: getPrivateMessageFromError(e) || "load-workspace-and-set-store-fc-error"
		});
		throw e;
	}
};

/**
 * used as impl to calculate saved workspace boundary
 */
export const calculateWorkspaceNodeBound = (node: IUserTemplateParams) => {
	const { x, y } = node.position!;
	const width = node.width!;
	const height = 200;
	return {
		left: x,
		right: x + width,
		top: y,
		bottom: y + height,
		width,
		height
	};
};

export const calculateBoundOfWorkspaceNodes = (nodes: IUserTemplateParams[]) => {
	return calculateBoundariesOfNodes(nodes, {
		calculateBoundariesOfNode: calculateWorkspaceNodeBound,
		calculateBoundariesOfObject: (sticker: any) =>
			calculateBoundariesOfObjectSerialized(sticker as CommentFrameWorkspaceDefinition)
	});
};

/**
 * mutates workspace config so nodes will be created in most right corner, fit camera to new nodes position
 */
export const mutWorkspaceToFreeSpace = (workspace: IWorkspace) => {
	if (!Editor.context.graph.nodes.length) return;
	const { right, top } = calculateBoundariesOfObjects(Editor.context.graph.nodes, [
		...Editor.context.graph.commentFrameMap.values()
	]);
	const workspaceBoundary = calculateBoundariesOfObjects(
		workspace.nodes,
		Object.values(workspace.stickers || {}),
		{
			calculateBoundariesOfNode: calculateWorkspaceNodeBound,
			calculateBoundariesOfObject: calculateBoundariesOfObjectSerialized
		}
	);

	const padding = 200;
	const moveToRightAmount = right - workspaceBoundary.left + padding;
	const moveToTopAmount = top - workspaceBoundary.top;
	const {
		left: leftEditor,
		top: topEditor,
		zoom
	} = moveEditorToBoundary({
		...workspaceBoundary,
		left: workspaceBoundary.left + moveToRightAmount,
		right: workspaceBoundary.right + moveToRightAmount,
		top: workspaceBoundary.top + moveToTopAmount,
		bottom: workspaceBoundary.bottom + moveToTopAmount,
		scalePadding: 2.5
	});
	workspace.editor.zoomScale = zoom;
	workspace.editor.position.x = leftEditor;
	workspace.editor.position.y = topEditor;
	for (const node of workspace.nodes) {
		if (node.position) {
			node.position.x += moveToRightAmount;
			node.position.y += moveToTopAmount;
		}
	}
	for (const key in workspace.stickers) {
		workspace.stickers[key].x += moveToRightAmount;
		workspace.stickers[key].y += moveToTopAmount;
	}
};

/**
 * mutates workspace config so nodes will be created at the specified position, fit camera to new nodes position
 * @param workspace workspace to mutate
 * @param targetX target X coordinate in editor space
 * @param targetY target Y coordinate in editor space
 */
export const mutWorkspaceToPosition = (workspace: IWorkspace, targetX: number, targetY: number) => {
	const workspaceBoundary = calculateBoundariesOfObjects(
		workspace.nodes,
		Object.values(workspace.stickers || {}),
		{
			calculateBoundariesOfNode: calculateWorkspaceNodeBound,
			calculateBoundariesOfObject: calculateBoundariesOfObjectSerialized
		}
	);

	// Calculate the offset to move workspace to target position
	const moveToRightAmount = targetX - workspaceBoundary.left;
	const moveToTopAmount = targetY - workspaceBoundary.top;

	const pos = Editor.context.graph.navigation.getPosition();
	workspace.editor.zoomScale = Editor.context.graph.navigation.getZoomScale();
	workspace.editor.position.x = pos.x;
	workspace.editor.position.y = pos.y;

	// Move all nodes to the new position
	for (const node of workspace.nodes) {
		if (node.position) {
			node.position.x += moveToRightAmount;
			node.position.y += moveToTopAmount;
		}
	}

	// Move all stickers to the new position
	for (const key in workspace.stickers) {
		workspace.stickers[key].x += moveToRightAmount;
		workspace.stickers[key].y += moveToTopAmount;
	}
};

/**
 * Loads template and positions it at specified coordinates
 * @param template template to load
 * @param targetX target X coordinate in editor space
 * @param targetY target Y coordinate in editor space
 */
export const loadTemplateToPosition = async (
	template: IWorkspaceTemplate,
	targetX: number,
	targetY: number
) => {
	mutWorkspaceLeagcyData(template.WorkspaceTemplate.workspace);
	mutWorkspaceToPosition(template.WorkspaceTemplate.workspace, targetX, targetY);
	updateWorkspaceUUids(template.WorkspaceTemplate.workspace);
	loadWorkspaceAndSetStore(
		template.WorkspaceTemplate,
		false,
		(workspace) => ({
			templateName: workspace.name,
			isStateSaved: true
		}),
		false
	);
};

/**
 * mutates workspace config, remove legacy wrong datatypes eg string position
 */
export const mutWorkspaceLeagcyData = (workspace: IWorkspace) => {
	for (const node of workspace.nodes) {
		if (node.position) {
			node.position.x = Number(node.position.x);
			node.position.y = Number(node.position.y);
		}
	}
};

/**
 * mutates workspace config, replace node uuids and uuids in connection with new uuids, so when user opens
 * one template several times, there is no collision
 */
export const updateWorkspaceUUids = (workspace: IWorkspace) => {
	// old uuid to new uuid
	const uuidsMap: Record<string, string> = {};
	const nodes = workspace.nodes;
	for (const templateParams of nodes) {
		const newUUID = generate_UUID();
		//save old uuid so we can find new uuid for connections
		if (templateParams.uuid) {
			uuidsMap[templateParams.uuid] = newUUID;
		}
		templateParams.uuid = newUUID;
	}

	for (const templateParams of nodes) {
		if (!templateParams.connections) continue;
		for (const connection of templateParams.connections) {
			if (connection.from.nodeUuid && connection.from.nodeUuid in uuidsMap) {
				// find new uuid of from Node by its old uuid
				connection.from.nodeUuid = uuidsMap[connection.from.nodeUuid];
			}
			if (connection.to.nodeUuid && connection.to.nodeUuid in uuidsMap) {
				connection.to.nodeUuid = uuidsMap[connection.to.nodeUuid];
			}
		}
	}
};

export const getIsRenameDisabled = () =>
	!(getWorkspacesStore().activeProjectId && !getWorkspacesStore().isOpenedByLink);

export const isSomeWorkspaceOppend = () => {
	const workspaceStore = getWorkspacesStore();
	if (workspaceStore.isOpenedByLink) return false;
	return !!workspaceStore.activeProjectId;
};

export const shareWorkspace = async () => {
	const activeWorkspaceId = getWorkspacesStore().activeProjectId;
	const activeWorkspaceLink = getWorkspacesStore().activeWorkspaceLink;
	EventAnalytics.shareProject(!activeWorkspaceLink);
	if (!activeWorkspaceId) return;
	if (!activeWorkspaceLink) {
		const linkResp = await WorkspaceApi.createWorkspaceLink(activeWorkspaceId);
		setWorkspacesStore((prev) => ({
			...prev,
			activeWorkspaceLink: linkResp.sharing_link
		}));
		return;
	}
	await WorkspaceApi.deleteWorkspaceLink(activeWorkspaceId);
	setWorkspacesStore((prev) => {
		prev.activeWorkspaceLink = null;
		return { ...prev };
	});
};
